/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { isFinalId } from './id-compressor';
import { assertWithMessage } from './Common';
/**
 * Create a {@link ContextualizedNodeIdNormalizer} that uses either the given session ID to normalize IDs
 * to session space. If no ID is given, it will use the local session ID belonging to the normalizer.
 */
export function scopeIdNormalizer(idNormalizer, sessionId) {
    return {
        normalizeToOpSpace: (id) => idNormalizer.normalizeToOpSpace(id),
        normalizeToSessionSpace: (id) => idNormalizer.normalizeToSessionSpace(id, sessionId !== null && sessionId !== void 0 ? sessionId : idNormalizer.localSessionId),
    };
}
/**
 * Create a {@link ContextualizedNodeIdNormalizer} that uses the local session ID belonging to the normalizer
 * to normalize IDs to session space. These IDs are expected to be sequenced, and will fail to normalize if
 * they are not.
 */
export function sequencedIdNormalizer(idNormalizer) {
    return {
        normalizeToOpSpace: (id) => {
            const normalized = idNormalizer.normalizeToOpSpace(id);
            assertWithMessage(isFinalId(normalized));
            return normalized;
        },
        normalizeToSessionSpace: (id) => {
            const normalized = idNormalizer.normalizeToSessionSpace(id, idNormalizer.localSessionId);
            assertWithMessage(isFinalId(normalized));
            return normalized;
        },
    };
}
export function getNodeIdContext(compressor) {
    return {
        generateNodeId: (override) => compressor.generateCompressedId(override),
        convertToNodeId: (id) => compressor.recompress(id),
        tryConvertToNodeId: (id) => compressor.tryRecompress(id),
        convertToStableNodeId: (id) => compressor.decompress(id),
        tryConvertToStableNodeId: (id) => compressor.tryDecompress(id),
        normalizeToOpSpace: (id) => compressor.normalizeToOpSpace(id),
        normalizeToSessionSpace: (id, sessionId) => compressor.normalizeToSessionSpace(id, sessionId),
        localSessionId: compressor.localSessionId,
    };
}
/** Accepts either a node or a node's identifier, and returns the identifier */
export function getNodeId(node) {
    var _a;
    return (_a = node.identifier) !== null && _a !== void 0 ? _a : node;
}
//# sourceMappingURL=NodeIdUtilities.js.map